import * as XLSX from 'xlsx';
import LocationsService from './LocationsService';

class ExcelExportService {
  constructor() {
    this.workbook = null;
  }

  createWorkbook() {
    this.workbook = XLSX.utils.book_new();
    return this.workbook;
  }

  addWorksheet(data, sheetName) {
    if (!this.workbook) {
      this.createWorkbook();
    }
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(this.workbook, worksheet, sheetName);
  }

  addWorksheetFromArray(data, sheetName, headers = null) {
    if (!this.workbook) {
      this.createWorkbook();
    }
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    if (headers) {
      XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });
    }
    XLSX.utils.book_append_sheet(this.workbook, worksheet, sheetName);
  }

  downloadWorkbook(filename) {
    if (!this.workbook) {
      throw new Error('No workbook created');
    }
    XLSX.writeFile(this.workbook, filename);
  }

  async fetchVendorIds(locationNames) {
    if (!locationNames || locationNames.length === 0) {
      return {};
    }
    try {
      const response = await LocationsService.getVendorIds(locationNames);
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor IDs:', error);
      return {};
    }
  }

  exportVRPRoute(route, vendorIdMap = {}) {
    this.createWorkbook();

    const routeSummary = this.createVRPSummaryData(route);
    this.addWorksheet(routeSummary, 'Rota Özeti');

    const vehicleRoutes = this.createVRPVehicleData(route);
    this.addWorksheet(vehicleRoutes, 'Araç Rotaları');

    const stopDetails = this.createVRPStopDetails(route, vendorIdMap);
    this.addWorksheet(stopDetails, 'Durak Detayları');

    const routeAnalysis = this.createVRPAnalysisData(route);
    this.addWorksheet(routeAnalysis, 'Rota Analizi');

    const filename = `VRP_Rota_${route.name || 'İsimsiz'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    this.downloadWorkbook(filename);
  }

  exportTSPRoute(route, vendorIdMap = {}) {
    this.createWorkbook();

    const routeSummary = this.createTSPSummaryData(route);
    this.addWorksheet(routeSummary, 'Rota Özeti');

    const stopSequence = this.createTSPStopSequence(route, vendorIdMap);
    this.addWorksheet(stopSequence, 'Durak Sırası');

    const timeAnalysis = this.createTSPTimeAnalysis(route);
    this.addWorksheet(timeAnalysis, 'Zaman Analizi');

    const filename = `TSP_Rota_${route.name || 'İsimsiz'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    this.downloadWorkbook(filename);
  }

  exportLAPRoute(route) {
    this.createWorkbook();

    const allocationSummary = this.createLAPSummaryData(route);
    this.addWorksheet(allocationSummary, 'Tahsis Özeti');

    const assignments = this.createLAPAssignments(route);
    this.addWorksheet(assignments, 'Tahsisler');

    const facilityUtilization = this.createLAPFacilityUtilization(route);
    this.addWorksheet(facilityUtilization, 'Tesis Kullanımı');

    const costAnalysis = this.createLAPCostAnalysis(route);
    this.addWorksheet(costAnalysis, 'Maliyet Analizi');

    const filename = `LAP_Tahsis_${route.name || 'İsimsiz'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    this.downloadWorkbook(filename);
  }

  createVRPSummaryData(route) {
    const carbonFootprint = this.calculateCarbonFootprint(route.total_distance);
    return [{
      'Rota Adı': route.name || 'İsimsiz',
      'Toplam Mesafe (km)': (route.total_distance / 1000).toFixed(2),
      'Toplam Süre': route.total_duration_formatted || this.formatDuration(route.total_duration),
      'Araç Sayısı': route.routes?.length || 0,
      'Toplam Durak': route.allLocations?.length || 0,
      'Durum': route.status || 'Bilinmiyor',
      'Karbon Ayak İzi (kg CO2)': carbonFootprint.toFixed(2),
      'Oluşturulma Tarihi': new Date(route.timestamp).toLocaleDateString('tr-TR')
    }];
  }

  createVRPVehicleData(route) {
    if (!route.routes) return [];
    
    return route.routes.map((vehicleRoute, index) => ({
      'Araç No': index + 1,
      'Araç ID': vehicleRoute.vehicleId || `Araç ${index + 1}`,
      'Durak Sayısı': vehicleRoute.stops?.length || 0,
      'Mesafe (km)': vehicleRoute.distance ? (vehicleRoute.distance / 1000).toFixed(2) : '0',
      'Süre': vehicleRoute.duration_formatted || this.formatDuration(vehicleRoute.duration_minutes),
      'Toplam Talep': this.calculateTotalDemand(vehicleRoute, route.demands)
    }));
  }

  createVRPStopDetails(route) {
    if (!route.routes) return [];
    
    const stopDetails = [];
    route.routes.forEach((vehicleRoute, vehicleIndex) => {
      if (vehicleRoute.reach_times) {
        vehicleRoute.reach_times.forEach((stop, stopIndex) => {
          const locationName = route.locationNames?.[stop.location_index] || `Lokasyon ${stop.location_index}`;
          const locationData = route.locationData?.[stop.location_index];
          const vendorId = locationData?.vendorId || '';
          const coordinates = route.allLocations?.[stop.location_index];
          
          stopDetails.push({
            'Araç ID': vehicleRoute.vehicleId || `Araç ${vehicleIndex + 1}`,
            'Durak Sırası': stopIndex + 1,
            'Lokasyon Adı': locationName,
            'Müşteri Kodu': vendorId,
            'Enlem': coordinates?.[1]?.toFixed(6) || '',
            'Boylam': coordinates?.[0]?.toFixed(6) || '',
            'Varış Zamanı': stop.reach_time_formatted || this.formatDuration(stop.reach_time_minutes),
            'Talep Miktarı': stop.demand || 0
          });
        });
      }
    });
    return stopDetails;
  }

  createVRPAnalysisData(route) {
    const totalDistance = route.total_distance || 0;
    const totalDuration = route.total_duration || 0;
    const vehicleCount = route.routes?.length || 0;
    const stopCount = route.allLocations?.length || 0;
    
    return [{
      'Ortalama Araç Mesafesi (km)': vehicleCount > 0 ? ((totalDistance / vehicleCount) / 1000).toFixed(2) : '0',
      'Ortalama Araç Süresi': vehicleCount > 0 ? this.formatDuration(totalDuration / vehicleCount) : '0',
      'Durak Başına Ortalama Mesafe (km)': stopCount > 0 ? ((totalDistance / stopCount) / 1000).toFixed(2) : '0',
      'Verimlilik Skoru': this.calculateEfficiencyScore(route),
      'Karbon Emisyonu (kg CO2/km)': '2.68',
      'Tahmini Yakıt Tüketimi (L)': (totalDistance * 0.08 / 1000).toFixed(2)
    }];
  }

  createTSPSummaryData(route) {
    const carbonFootprint = this.calculateCarbonFootprint(route.total_distance);
    return [{
      'Rota Adı': route.name || 'İsimsiz',
      'Toplam Mesafe (km)': (route.total_distance / 1000).toFixed(2),
      'Toplam Süre': route.total_duration_formatted || this.formatDuration(route.total_duration),
      'Durak Sayısı': route.allLocations?.length || 0,
      'Durum': route.status || 'Bilinmiyor',
      'Karbon Ayak İzi (kg CO2)': carbonFootprint.toFixed(2),
      'Oluşturulma Tarihi': new Date(route.timestamp).toLocaleDateString('tr-TR')
    }];
  }

  createTSPStopSequence(route, vendorIdMap = {}) {
    if (!route.routes?.[0]?.reach_times) return [];
    
    return route.routes[0].reach_times.map((stop, index) => {
      const locationName = route.locationNames?.[stop.location_index] || `Lokasyon ${stop.location_index}`;
      const locationData = route.locationData?.[stop.location_index];
      const vendorId = locationData?.vendorId || vendorIdMap[locationName] || '';
      const coordinates = route.allLocations?.[stop.location_index];
      
      return {
        'Durak Sırası': index + 1,
        'Lokasyon Adı': locationName,
        'Müşteri Kodu': vendorId,
        'Enlem': coordinates?.[1]?.toFixed(6) || '',
        'Boylam': coordinates?.[0]?.toFixed(6) || '',
        'Varış Zamanı': stop.reach_time_formatted || this.formatDuration(stop.reach_time_minutes),
        'Talep Miktarı': stop.demand || 0
      };
    });
  }

  createTSPTimeAnalysis(route) {
    if (!route.routes?.[0]?.reach_times) return [];
    
    const stops = route.routes[0].reach_times;
    return stops.map((stop, index) => {
      const nextStop = stops[index + 1];
      const travelTime = nextStop ? nextStop.reach_time_minutes - stop.reach_time_minutes : 0;
      
      return {
        'Durak': index + 1,
        'Varış Zamanı': stop.reach_time_formatted || this.formatDuration(stop.reach_time_minutes),
        'Bir Sonraki Durağa Süre (dk)': travelTime > 0 ? Math.round(travelTime) : 'Son Durak',
        'Toplam Geçen Süre (dk)': Math.round(stop.reach_time_minutes)
      };
    });
  }

  createLAPSummaryData(route) {
    return [{
      'Tahsis Adı': route.name || 'İsimsiz',
      'Toplam Maliyet': route.total_cost?.toFixed(2) || '0',
      'Ortalama Mesafe (km)': route.average_distance ? (route.average_distance / 1000).toFixed(2) : '0',
      'Tahsis Sayısı': route.num_assignments || 0,
      'Tesis Sayısı': route.facilities?.length || 0,
      'Talep Noktası Sayısı': route.demands?.length || 0,
      'Durum': route.status || 'Bilinmiyor',
      'Oluşturulma Tarihi': new Date(route.timestamp).toLocaleDateString('tr-TR')
    }];
  }

  createLAPAssignments(route) {
    if (!route.assignments) return [];
    
    return route.assignments.map((assignment, index) => {
      const facility = route.facilities?.[assignment.facility_idx];
      const demand = route.demands?.[assignment.demand_idx];
      
      return {
        'Tahsis No': index + 1,
        'Tesis Adı': facility?.name || `Tesis ${assignment.facility_idx}`,
        'Talep Noktası': demand?.name || `Talep ${assignment.demand_idx}`,
        'Mesafe (km)': assignment.distance ? (assignment.distance / 1000).toFixed(2) : '0',
        'Maliyet': assignment.cost?.toFixed(2) || '0',
        'Talep Miktarı': assignment.demand || 0
      };
    });
  }

  createLAPFacilityUtilization(route) {
    if (!route.facility_utilization) return [];
    
    return route.facility_utilization.map((utilization, index) => {
      const facility = route.facilities?.[index];
      
      return {
        'Tesis Adı': facility?.name || `Tesis ${index + 1}`,
        'Kapasite': utilization.capacity || 0,
        'Kullanılan': utilization.load || 0,
        'Kullanım Oranı (%)': ((utilization.utilization_rate || 0) * 100).toFixed(1),
        'Kalan Kapasite': utilization.remaining_capacity || 0
      };
    });
  }

  createLAPCostAnalysis(route) {
    if (!route.assignments) return [];
    
    const costAnalysis = {};
    route.assignments.forEach(assignment => {
      const facilityName = route.facilities?.[assignment.facility_idx]?.name || `Tesis ${assignment.facility_idx}`;
      if (!costAnalysis[facilityName]) {
        costAnalysis[facilityName] = {
          'Tesis Adı': facilityName,
          'Toplam Maliyet': 0,
          'Tahsis Sayısı': 0,
          'Ortalama Maliyet': 0
        };
      }
      costAnalysis[facilityName]['Toplam Maliyet'] += assignment.cost || 0;
      costAnalysis[facilityName]['Tahsis Sayısı'] += 1;
    });
    
    return Object.values(costAnalysis).map(facility => ({
      ...facility,
      'Toplam Maliyet': facility['Toplam Maliyet'].toFixed(2),
      'Ortalama Maliyet': (facility['Toplam Maliyet'] / facility['Tahsis Sayısı']).toFixed(2)
    }));
  }

  calculateCarbonFootprint(distanceInMeters) {
    const distanceInKm = distanceInMeters / 1000;
    const carbonEmissionFactor = 2.68;
    return distanceInKm * carbonEmissionFactor;
  }

  calculateTotalDemand(vehicleRoute, demands) {
    if (!vehicleRoute.reach_times || !demands) return 0;
    return vehicleRoute.reach_times.reduce((total, stop) => {
      return total + (stop.demand || 0);
    }, 0);
  }

  calculateEfficiencyScore(route) {
    const totalDistance = route.total_distance || 0;
    const stopCount = route.allLocations?.length || 1;
    
    const avgDistancePerStop = totalDistance / stopCount;
    const baseEfficiency = Math.max(0, 100 - (avgDistancePerStop / 10000));
    
    return Math.min(100, Math.max(0, baseEfficiency)).toFixed(1);
  }

  formatDuration(minutes) {
    if (!minutes) return '00:00';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  getSolverType(route) {
    if (route.facilities && route.assignments) return 'LAP';
    if (route.routes && route.routes.length === 1) return 'TSP';
    if (route.routes && route.routes.length > 1) return 'VRP';
    return 'UNKNOWN';
  }

  async exportRoute(route) {
    const solverType = this.getSolverType(route);
    
    // Fetch vendor IDs
    const locationNames = route.locationNames || [];
    const vendorIdMap = await this.fetchVendorIds(locationNames);

    switch (solverType) {
      case 'VRP':
        this.exportVRPRoute(route, vendorIdMap);
        break;
      case 'TSP':
        this.exportTSPRoute(route, vendorIdMap);
        break;
      case 'LAP':
        this.exportLAPRoute(route);
        break;
      default:
        throw new Error('Desteklenmeyen rota tipi');
    }
  }
}

export default new ExcelExportService();